import React, { createContext, useContext, useReducer, useEffect } from 'react';

const AppContext = createContext();

// Currency mapping based on country
const CURRENCY_MAP = {
  'India': { code: 'INR', symbol: '₹' },
  'USA': { code: 'USD', symbol: '$' },
  'UK': { code: 'GBP', symbol: '£' },
  'UAE': { code: 'AED', symbol: 'AED' }
};

const initialState = {
  currentProduct: null,
  scanHistory: [],
  wishlist: [],
  activeTab: 'home',
  isScanning: false,
  currentScreen: 'home',
  selectedCountry: localStorage.getItem('atelia_country') || 'India',
  scanData: {
    url: { active: false, data: '' },
    tag: { active: false, images: [] },
    barcode: { active: false, data: '' },
    photos: { active: false, images: [] }
  },
  toast: { visible: false, message: '', type: 'info' }
};

const appReducer = (state, action) => {
  switch (action.type) {
    case 'SET_CURRENT_PRODUCT':
      return {
        ...state,
        currentProduct: action.payload,
        scanHistory: state.scanHistory.some(p => p.id === action.payload.id)
          ? state.scanHistory
          : [action.payload, ...state.scanHistory].slice(0, 10)
      };
    case 'SET_ACTIVE_TAB':
      return {
        ...state,
        activeTab: action.payload
      };
    case 'SET_CURRENT_SCREEN':
      return {
        ...state,
        currentScreen: action.payload
      };
    case 'SET_SCANNING':
      return {
        ...state,
        isScanning: action.payload
      };
    case 'ADD_TO_WISHLIST':
      if (state.wishlist.some(item => item.id === action.payload.id)) {
        return state;
      }
      return {
        ...state,
        wishlist: [...state.wishlist, action.payload]
      };
    case 'SET_COUNTRY':
      localStorage.setItem('atelia_country', action.payload);
      return {
        ...state,
        selectedCountry: action.payload
      };
    case 'UPDATE_SCAN_DATA':
      return {
        ...state,
        scanData: { ...state.scanData, ...action.payload }
      };
    case 'RESET_SCAN_DATA':
      return {
        ...state,
        scanData: {
          url: { active: false, data: '' },
          tag: { active: false, images: [] },
          barcode: { active: false, data: '' },
          photos: { active: false, images: [] }
        }
      };
    case 'SHOW_TOAST':
      return {
        ...state,
        toast: { visible: true, message: action.payload.message, type: action.payload.type }
      };
    case 'HIDE_TOAST':
      return {
        ...state,
        toast: { visible: false, message: '', type: 'info' }
      };
    case 'REMOVE_FROM_WISHLIST':
      return {
        ...state,
        wishlist: state.wishlist.filter(item => item.id !== action.payload)
      };
    case 'CLEAR_WISHLIST':
      return {
        ...state,
        wishlist: []
      };
    case 'CLEAR_HISTORY':
      return {
        ...state,
        scanHistory: []
      };
    default:
      return state;
  }
};

export const AppProvider = ({ children }) => {
  const [state, dispatch] = useReducer(appReducer, initialState, (initial) => {
    const savedWishlist = localStorage.getItem('fashIntelWishlist');
    const savedHistory = localStorage.getItem('fashIntelHistory');
    return {
      ...initial,
      wishlist: savedWishlist ? JSON.parse(savedWishlist) : [],
      scanHistory: savedHistory ? JSON.parse(savedHistory) : []
    };
  });

  useEffect(() => {
    localStorage.setItem('fashIntelWishlist', JSON.stringify(state.wishlist));
  }, [state.wishlist]);

  useEffect(() => {
    // Strip full imageUrl (large base64) but keep thumbnailUrl (small resized version)
    const historyToSave = state.scanHistory.map(({ imageUrl, ...rest }) => rest);
    localStorage.setItem('fashIntelHistory', JSON.stringify(historyToSave));
  }, [state.scanHistory]);


  const setCurrentProduct = (product) => {
    dispatch({ type: 'SET_CURRENT_PRODUCT', payload: product });
  };

  const setActiveTab = (tab) => {
    dispatch({ type: 'SET_ACTIVE_TAB', payload: tab });
  };

  const setCurrentScreen = (screen) => {
    dispatch({ type: 'SET_CURRENT_SCREEN', payload: screen });
  };

  const setScanning = (isScanning) => {
    dispatch({ type: 'SET_SCANNING', payload: isScanning });
  };

  const addToWishlist = (item) => {
    dispatch({ type: 'ADD_TO_WISHLIST', payload: item });
  };

  const removeFromWishlist = (itemId) => {
    dispatch({ type: 'REMOVE_FROM_WISHLIST', payload: itemId });
  };

  const isInWishlist = (itemId) => {
    return state.wishlist.some(item => item.id === itemId);
  };

  const clearWishlist = () => {
    dispatch({ type: 'CLEAR_WISHLIST' });
  };

  const clearHistory = () => {
    dispatch({ type: 'CLEAR_HISTORY' });
  };

  const setCountry = (country) => {
    dispatch({ type: 'SET_COUNTRY', payload: country });
  };

  const getCurrency = () => {
    return CURRENCY_MAP[state.selectedCountry] || CURRENCY_MAP['India'];
  };

  const updateScanData = (data) => {
    dispatch({ type: 'UPDATE_SCAN_DATA', payload: data });
  };

  const resetScanData = () => {
    dispatch({ type: 'RESET_SCAN_DATA' });
  };

  const showToast = (message, type = 'info') => {
    dispatch({ type: 'SHOW_TOAST', payload: { message, type } });
  };

  const hideToast = () => {
    dispatch({ type: 'HIDE_TOAST' });
  };

  return (
    <AppContext.Provider
      value={{
        ...state,
        setCurrentProduct,
        setActiveTab,
        setCurrentScreen,
        setScanning,
        addToWishlist,
        removeFromWishlist,
        isInWishlist,
        clearWishlist,
        clearHistory,
        setCountry,
        getCurrency,
        updateScanData,
        resetScanData,
        showToast,
        hideToast
      }}
    >
      {children}
    </AppContext.Provider>
  );
};

export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
};

export default AppContext;
